import { LineItem } from 'src/types/application/SalesOrderDraftDTO';
import { useProduct } from 'src/api/inventory-management/products/useProduct';
import useProductVariant from 'src/api/inventory-management/products/useProductVariant';
import useCustomerProduct from '@/api/inventory-management/products/useCustomerProduct';
import { T, Var } from 'gt-react';

type Props = {
  item?: LineItem;
  label?: string;
};

export function hasMatch(item: LineItem) {
  return !!(
    item.selectedCustomerProductId ||
    item.selectedProductVariantId ||
    item.suggestedCustomerProductId ||
    item.suggestedProductVariantId
  );
}

const CustomerProductMatch = ({ item, label = 'Variant' }: Props) => {
  const productId = item?.suggestedProductId;
  const customerProductId =
    item?.selectedCustomerProductId || item?.suggestedCustomerProductId;
  const variantId =
    item?.selectedProductVariantId || item?.suggestedProductVariantId;

  const { data: productData, isLoading: isProductLoading } = useProduct(
    productId,
    undefined,
    !!productId,
  );
  const { data: variantData, isLoading: isVariantLoading } = useProductVariant(
    variantId || '',
    { enabled: !!variantId && !customerProductId },
  );
  const { data: customerProductData, isLoading: isCustomerProductLoading } =
    useCustomerProduct(customerProductId, !!customerProductId);

  const productName = productData?.product?.name;
  const customerProductName =
    customerProductData?.customerProduct?.productVariant?.name;
  const variantName =
    variantData?.productVariant?.originMetadata?.NAMEINVC ||
    customerProductName ||
    variantData?.productVariant?.sku ||
    customerProductData?.customerProduct?.product?.name;
  const productIdFromMetadata =
    variantData?.productVariant?.originMetadata?.PRODUCT_ID;

  // Prefer variant info when available; otherwise show product info
  if (variantId || customerProductId) {
    if (isVariantLoading || isCustomerProductLoading) {
      return (
        <span className="text-xs text-muted-foreground">
          <T>
            <Var>{label}</Var>: Loading...
          </T>
        </span>
      );
    }
    if (variantName) {
      return (
        <span className="text-xs text-muted-foreground">
          <span className="font-semibold">
            <T>
              <Var>{label}</Var>:
            </T>
          </span>{' '}
          {String(variantName)}
          {productIdFromMetadata ? ` (${String(productIdFromMetadata)})` : ''}
        </span>
      );
    }
    return null;
  }

  if (productId) {
    if (isProductLoading) {
      return (
        <span className="text-xs text-muted-foreground">
          <T>
            <Var>{label}</Var>: Loading...
          </T>
        </span>
      );
    }
    if (productName) {
      return (
        <span className="text-xs text-muted-foreground">
          <span className="font-semibold">
            <T>
              <Var>{label}</Var>:
            </T>
          </span>{' '}
          {String(productName)}
        </span>
      );
    }
  }

  return null;
};

export default CustomerProductMatch;
